/**
 * Onboarding Hooks & Utilities
 * 
 * React hooks for managing tenant onboarding flow:
 * - useOnboardingStatus() - Get current onboarding progress
 * - useOnboardingChecklist() - Get full checklist
 * - useCompleteStep() - Mark step as complete
 * - useSkipOnboarding() - Skip remaining onboarding
 */

import { useAuthToken } from '../auth';
import { useState, useEffect, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export type OnboardingStep =
  | 'create_account'
  | 'verify_email'
  | 'company_profile'
  | 'connect_data_source'
  | 'create_strategy'
  | 'review_results'
  | 'invite_team'
  | 'setup_billing';

export interface StepStatus {
  step: OnboardingStep;
  display_name: string;
  description: string;
  is_required: boolean;
  is_completed: boolean;
  completed_at: string | null;
  is_skipped: boolean;
  order: number;
  action_url: string | null;
}

export interface OnboardingStatus {
  is_complete: boolean;
  is_skipped: boolean;
  progress_percent: number;
  completed_steps: number;
  total_steps: number;
  required_steps_complete: boolean;
  current_step: OnboardingStep | null;
  steps: StepStatus[];
  started_at: string;
  completed_at: string | null;
}

export interface CompleteStepResponse {
  success: boolean;
  step: string;
  message: string;
  next_step: OnboardingStep | null;
  progress_percent: number;
}

export interface ChecklistItem {
  id: OnboardingStep;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
  action_url: string | null;
}

export interface ChecklistResponse {
  checklist: ChecklistItem[];
  progress: number;
  is_complete: boolean;
}

// ============================================================================
// API Configuration
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || '';

async function fetchWithAuth(
  url: string,
  token: string | null,
  options: RequestInit = {}
): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get current onboarding status
 */
export function useOnboardingStatus() {
  const { getToken } = useAuthToken();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await fetchWithAuth(
        `${API_BASE}/api/onboarding/status`,
        token
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch onboarding status');
      }

      const data: OnboardingStatus = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
    // Computed helpers
    isComplete: status?.is_complete ?? false,
    isSkipped: status?.is_skipped ?? false,
    currentStep: status?.current_step ?? null,
    progressPercent: status?.progress_percent ?? 0,
  };
}

/**
 * Hook to get onboarding checklist
 */
export function useOnboardingChecklist() {
  const { getToken } = useAuthToken();
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChecklist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await fetchWithAuth(
        `${API_BASE}/api/onboarding/checklist`,
        token
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch checklist');
      }

      const data: ChecklistResponse = await response.json();
      setChecklist(data.checklist);
      setProgress(data.progress);
      setIsComplete(data.is_complete);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  return {
    checklist,
    progress,
    isComplete,
    loading,
    error,
    refetch: fetchChecklist,
    // Filter helpers
    requiredSteps: checklist.filter(item => item.required),
    optionalSteps: checklist.filter(item => !item.required),
    completedSteps: checklist.filter(item => item.completed),
    pendingSteps: checklist.filter(item => !item.completed),
  };
}

/**
 * Hook to complete an onboarding step
 */
export function useCompleteStep() {
  const { getToken } = useAuthToken();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<CompleteStepResponse | null>(null);

  const completeStep = useCallback(
    async (step: OnboardingStep, data?: Record<string, unknown>) => {
      try {
        setLoading(true);
        setError(null);
        const token = await getToken();
        const response = await fetchWithAuth(
          `${API_BASE}/api/onboarding/steps/${step}/complete`,
          token,
          {
            method: 'POST',
            body: JSON.stringify({ data }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to complete step');
        }

        const result: CompleteStepResponse = await response.json();
        setLastResult(result);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getToken]
  );

  return {
    completeStep,
    loading,
    error,
    lastResult,
  };
}

/**
 * Hook to skip onboarding
 */
export function useSkipOnboarding() {
  const { getToken } = useAuthToken();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skipOnboarding = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await fetchWithAuth(
        `${API_BASE}/api/onboarding/skip`,
        token,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to skip onboarding');
      }

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  return {
    skipOnboarding,
    loading,
    error,
  };
}

/**
 * Combined hook for all onboarding operations
 */
export function useOnboarding() {
  const status = useOnboardingStatus();
  const checklist = useOnboardingChecklist();
  const completeStepHook = useCompleteStep();
  const skipHook = useSkipOnboarding();

  const refreshAll = useCallback(() => {
    status.refetch();
    checklist.refetch();
  }, [status.refetch, checklist.refetch]);

  // Auto-refresh after completing a step
  const completeStep = useCallback(
    async (step: OnboardingStep, data?: Record<string, unknown>) => {
      const result = await completeStepHook.completeStep(step, data);
      refreshAll();
      return result;
    },
    [completeStepHook, refreshAll]
  );

  // Auto-refresh after skipping
  const skipOnboarding = useCallback(async () => {
    const result = await skipHook.skipOnboarding();
    refreshAll();
    return result;
  }, [skipHook, refreshAll]);

  return {
    // Status
    status: status.status,
    isComplete: status.isComplete,
    isSkipped: status.isSkipped,
    currentStep: status.currentStep,
    progressPercent: status.progressPercent,

    // Checklist
    checklist: checklist.checklist,
    requiredSteps: checklist.requiredSteps,
    optionalSteps: checklist.optionalSteps,

    // Loading states
    loading: status.loading || checklist.loading,
    actionLoading: completeStepHook.loading || skipHook.loading,
    error: status.error || checklist.error,

    // Actions
    completeStep,
    skipOnboarding,
    refresh: refreshAll,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get icon name for each step
 */
export function getStepIcon(step: OnboardingStep): string {
  switch (step) {
    case 'create_account':
      return 'UserPlus';
    case 'verify_email':
      return 'Mail';
    case 'company_profile':
      return 'Building';
    case 'connect_data_source':
      return 'Database';
    case 'create_strategy':
      return 'MessageCircle';
    case 'review_results':
      return 'BarChart';
    case 'invite_team':
      return 'Users';
    case 'setup_billing':
      return 'CreditCard';
    default:
      return 'CheckCircle';
  }
}

/**
 * Get step color based on status
 */
export function getStepColor(completed: boolean, isCurrent: boolean): string {
  if (completed) return 'green';
  if (isCurrent) return 'blue';
  return 'gray';
}

/**
 * Get estimated time for each step
 */
export function getStepEstimatedTime(step: OnboardingStep): string {
  switch (step) {
    case 'create_account':
      return '1 min';
    case 'verify_email':
      return '2 min';
    case 'company_profile':
      return '3 min';
    case 'connect_data_source':
      return '5 min';
    case 'create_strategy':
      return '10 min';
    case 'review_results':
      return '5 min';
    case 'invite_team':
      return '3 min';
    case 'setup_billing':
      return '2 min';
    default:
      return '5 min';
  }
}

/**
 * Format step name for display
 */
export function formatStepName(step: OnboardingStep): string {
  return step
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Check if user should see onboarding
 */
export function shouldShowOnboarding(status: OnboardingStatus | null): boolean {
  if (!status) return false;
  return !status.is_complete && !status.is_skipped;
}

/**
 * Get progress message
 */
export function getProgressMessage(status: OnboardingStatus): string {
  if (status.is_complete) {
    return 'All set -- your desk is ready.';
  }
  if (status.is_skipped) {
    return 'Setup skipped -- pick it up anytime from Settings.';
  }
  const remaining = status.total_steps - status.completed_steps;
  return `${remaining} step${remaining === 1 ? '' : 's'} remaining`;
}

/**
 * Calculate total estimated time remaining
 */
export function getEstimatedTimeRemaining(
  checklist: ChecklistItem[]
): string {
  const pendingSteps = checklist.filter(item => !item.completed);
  
  let totalMinutes = 0;
  for (const item of pendingSteps) {
    const time = getStepEstimatedTime(item.id);
    const timeParts = time.split(' ');
    const minutes = parseInt(timeParts[0] || '0', 10);
    totalMinutes += minutes;
  }

  if (totalMinutes === 0) return 'Done!';
  if (totalMinutes < 60) return `~${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return mins > 0 ? `~${hours}h ${mins}m` : `~${hours}h`;
}

// ============================================================================
// Step Configuration (for rendering)
// ============================================================================

export const ONBOARDING_STEPS: Record<
  OnboardingStep,
  {
    title: string;
    description: string;
    actionLabel: string;
    helpText: string;
  }
> = {
  create_account: {
    title: 'Create Account',
    description: 'Your account has been created successfully.',
    actionLabel: 'Continue',
    helpText: 'Account creation is automatic upon signup.',
  },
  verify_email: {
    title: 'Verify Email',
    description: 'Verify your email address to secure your account.',
    actionLabel: 'Verify Email',
    helpText: 'Check your inbox for a verification link.',
  },
  company_profile: {
    title: 'Company Profile',
    description: 'Tell us how much capital you\'re trading so the AI can ground its starting-capital default in your firm\'s real scale.',
    actionLabel: 'Set Up Profile',
    helpText: 'Takes 5 seconds -- optional, and can be changed anytime in Settings.',
  },
  connect_data_source: {
    title: 'Connect Data',
    description: 'Connect an exchange API or upload historical data.',
    actionLabel: 'Add Data Source',
    helpText: 'You need data to backtest strategies.',
  },
  create_strategy: {
    title: 'Find a Strategy',
    description: 'Browse the strategy library and find one that fits your mandate.',
    actionLabel: 'Browse Library',
    helpText: 'Every listed strategy is documented and backtested -- pick one that matches your mandate.',
  },
  review_results: {
    title: 'Review Results',
    description: 'Analyze your backtest results and performance metrics.',
    actionLabel: 'View Results',
    helpText: 'Key metrics: Sharpe ratio, drawdown, win rate.',
  },
  invite_team: {
    title: 'Invite Team',
    description: 'Collaborate by inviting team members (optional).',
    actionLabel: 'Invite Members',
    helpText: 'Available on Pro plan and above.',
  },
  setup_billing: {
    title: 'Set Up Billing',
    description: 'Add a payment method to unlock premium features.',
    actionLabel: 'Add Payment',
    helpText: 'Free tier has limited features.',
  },
};

export default useOnboarding;
